import type { Card } from './data/cards/types';
import { cardsByTier, getHeroBooster } from './data/cards';
import { allHeroes, type Hero, type HeroId } from './data/heroes';
import { rollCardTier } from './data/gameConfig';
import { newInstance } from './shop';
import type { RNG } from './rng';
import type { AIPlayer } from './lobby';

// R5–R7 single-player AI lifecycle. Replaces the throwaway opponent
// generator with full AI players that persist across rounds, run their
// own tavern phases, and are paired against each other for silent
// fights. Legacy `generateOpponentBoard` is kept as a low-cost shim
// for the very first round (before any AI has had a tavern tick) and
// for any direct callers.

/**
 * Pick `count` distinct heroes for AI opponents, excluding the human's
 * pick. Deterministic given the RNG. Throws if the hero pool can't
 * support the request.
 */
export function pickAIHeroes(
  count: number,
  excludeHeroId: HeroId | null,
  rng: RNG,
): Hero[] {
  const pool = allHeroes.filter((h) => h.id !== excludeHeroId);
  if (pool.length < count) {
    throw new Error(
      `pickAIHeroes: requested ${count} but only ${pool.length} heroes available`,
    );
  }
  return rng.shuffle(pool).slice(0, count);
}

/**
 * Build a fresh AI starting board from a hero's booster. We don't run
 * the draft pick UX for bots — we just give them the same kind of
 * starter mix the human got and place 3 of those cards on the board.
 * Anything left over becomes their starting "hand" (currently unused
 * by AI; reserved for future flexibility).
 */
export function createAIPlayer(
  id: string,
  hero: Hero,
  rng: RNG,
): AIPlayer {
  // 6-card booster, same shape the human draft uses. The shuffle inside
  // getHeroBooster uses Math.random — that's fine for an opening-state
  // splash of variety since the AI's per-round behavior is what matters
  // for determinism. (We re-shuffle via rng below anyway.)
  const booster = getHeroBooster(hero.id, 6);
  const shuffled = rng.shuffle(booster);
  const starting = shuffled.slice(0, 3).map(newInstance);

  const board: (Card | null)[] = Array(7).fill(null);
  starting.forEach((card, i) => {
    board[i] = card;
  });

  return {
    id,
    name: `Bot ${id.replace(/^P/, '')}`,
    heroId: hero.id,
    hero,
    health: 30,
    gold: 3,
    tavernTier: 1,
    turnsAtCurrentTier: 0,
    board,
    hand: [],
    alive: true,
    heroPowerUsedThisTurn: false,
    refreshCountThisTurn: 0,
  };
}

/**
 * Legacy synthetic opponent. Used only as a safety fallback (e.g. an
 * AI with an empty board pre-tavern-tick on round 1). Kept tier-aware
 * so it tracks the difficulty curve.
 */
export function generateOpponentBoard(round: number, tavernTier: number = 1): Card[] {
  const size = Math.min(7, Math.max(3, round + 1));
  const board: Card[] = [];
  for (let i = 0; i < size; i++) {
    const tier = rollCardTier(tavernTier);
    const pool = cardsByTier[tier] || cardsByTier[1];
    const template = pool[Math.floor(Math.random() * pool.length)];
    board.push(newInstance(template));
  }
  return board;
}
